// ── NorbiPhone Analytics ──────────────────────────────────────────────────────
// Egyszerű wrapper – nem kell overengineerelni.
// Ha hibázik, csendben elnyeli – soha ne törje el az alkalmazást.
import { clarityEvent } from './clarity';

const prod = process.env.NODE_ENV === 'production';

// ── Debug mód ────────────────────────────────────────────────────────────────
// `window.analyticsDebug = true` → minden kimenő esemény a konzolra kerül,
// platformonként. Fejlesztéshez és élesben is használható (a konzol
// megnyitása után), mert a mérést semmiben nem befolyásolja.
const dbg = (platform, event, params) => {
  try {
    if (!window.analyticsDebug) return;
    // eslint-disable-next-line no-console
    console.log(`%c[Analytics] %c${platform} %c${event}`,
      'color:#39B54A;font-weight:bold', 'color:#888', 'color:#111;font-weight:bold',
      params);
  } catch { /* a debug-log SOSEM törhet appot */ }
};

// ── Automatikus közös paraméterek ────────────────────────────────────────────
// MINDEN eseményhez csatolódik, hogy a riportokban ne kelljen összefésülni:
// page/path (hol történt), referrer (honnan jött), device (mobil/tablet/desktop),
// timestamp, valamint a kampány-forrás (utm_source/medium/campaign vagy gclid).
// A store és az egyéb entitás-mezők a deviceParams-ból jönnek (ld. lentebb).
const deviceType = () => {
  try {
    const w = window.innerWidth || 0;
    if (w && w < 640) return 'mobile';
    if (w && w < 1024) return 'tablet';
    return 'desktop';
  } catch { return 'unknown'; }
};

// A kampány-adatot az ELSŐ oldalbetöltéskor rögzítjük: a felhasználó később
// belső linkeken navigál, ahol az utm már nincs az URL-ben – enélkül a
// konverzió „direct”-ként látszana.
let campaignCache = null;
const campaignParams = () => {
  if (campaignCache) return campaignCache;
  const out = {};
  try {
    const q = new URLSearchParams(window.location.search);
    const put = (key, value) => { if (value) out[key] = value; };
    put('utm_source', q.get('utm_source'));
    put('utm_medium', q.get('utm_medium'));
    put('utm_campaign', q.get('utm_campaign'));
    put('utm_content', q.get('utm_content'));
    if (q.get('gclid')) out.gclid = q.get('gclid');
    if (q.get('szolg')) out.ad_group = q.get('szolg');   // a landing hirdetéscsoportja
  } catch { /* URL-hiba nem törhet mérést */ }
  campaignCache = out;
  return out;
};

const commonParams = () => {
  try {
    return {
      page_path: window.location.pathname || '',
      page_location: window.location.href || '',
      page_referrer: document.referrer || '',
      device_type: deviceType(),
      event_ts: Date.now(),
      ...campaignParams(),
    };
  } catch { return {}; }
};

// ── Alap hívók ────────────────────────────────────────────────────────────────
// Mindegyik: (1) hozzáfűzi a közös paramétereket, (2) debug-logol,
// (3) hiba esetén CSENDBEN továbblép – hiányzó platform sosem dob.
const fb  = (event, params = {}) => { const p = { ...commonParams(), ...params }; dbg('meta', event, p); try { if (prod && window.fbq) window.fbq('track', event, p); } catch {} };
const tt  = (event, params = {}) => { const p = { ...commonParams(), ...params }; dbg('tiktok', event, p); try { if (prod && window.ttq) window.ttq.track(event, p); } catch {} };
// Meta EGYEDI esemény (trackCustom) – a standard eseménykészleten kívüli nevekhez
const fbc = (event, params = {}) => { const p = { ...commonParams(), ...params }; dbg('meta:custom', event, p); try { if (prod && window.fbq) window.fbq('trackCustom', event, p); } catch {} };
const ga  = (event, params = {}) => { const p = { ...commonParams(), ...params }; dbg('ga4', event, p); try { if (prod && window.gtag) window.gtag('event', event, p); } catch {} };
// PostHog – a loader a public/index.html-ben van (consent-gated, statisztikai
// hozzájárulás után). Itt csak akkor küldünk, ha a stub/SDK tényleg jelen van.
const ph  = (event, params = {}) => { const p = { ...commonParams(), ...params }; dbg('posthog', event, p); try { if (prod && window.posthog && window.posthog.capture) window.posthog.capture(event, p); } catch {} };

// ── Egyszeri-tüzelés őr (FELADAT 4) ──────────────────────────────────────────
// Két külön igény:
//  • „view” típusú esemény (product_view, price_list_open) oldalanként/entitásonként
//    PONTOSAN egyszer fusson – React StrictMode dupla-render, remount, vissza-
//    navigáció ne duplikálja.
//  • „click” típusú esemény ne tüzeljen kétszer EGY interakcióból (dupla handler,
//    onClick+onTouchEnd, gyors dupla-koppintás), de a felhasználó valódi, későbbi
//    újra-kattintása LEGYEN mérhető → rövid idő-ablakos dedup.
const seenOnce = new Set();          // view-események kulcsai
const recentClicks = new Map();      // click-kulcs → utolsó tüzelés ms
const CLICK_DEDUPE_MS = 1000;   // 1 mp – ugyanaz az esemény ezen belül nem megy ki újra

// Útvonalváltáskor a view-kulcsok ürülnek (SPA: új oldal = új mérés).
let lastPath = typeof window !== 'undefined' ? window.location.pathname : '';
const resetIfNavigated = () => {
  if (typeof window === 'undefined') return;
  if (window.location.pathname !== lastPath) {
    lastPath = window.location.pathname;
    seenOnce.clear();
    recentClicks.clear();
  }
};

/** true, ha MOST szabad tüzelni. `mode`: 'once' (oldalanként egyszer) | 'click'. */
const allowFire = (key, mode = 'once') => {
  try {
    resetIfNavigated();
    if (mode === 'click') {
      const now = Date.now();
      const prev = recentClicks.get(key) || 0;
      if (now - prev < CLICK_DEDUPE_MS) return false;
      recentClicks.set(key, now);
      return true;
    }
    if (seenOnce.has(key)) return false;
    seenOnce.add(key);
    return true;
  } catch { return true; }   // a mérés sosem blokkolhatja az UI-t
};

/** Az aktuális oldal útvonala – minden esemény `source_page` paramétere. */
const sourcePage = () => { try { return window.location.pathname || ''; } catch { return ''; } };

/** Készülék-kontextus → egységes esemény-paraméterek (FELADAT 4).
 *  Csak a MEGLÉVŐ mezőket küldi ki (nincs undefined/null zaj a riportokban).
 *  Elfogad nyers listing/device objektumot ÉS kézzel összeállított ctx-et. */
export const deviceParams = (input) => {
  const d = input || {};   // null/undefined is biztonságos
  const out = {};
  const put = (k, v) => { if (v !== undefined && v !== null && v !== '') out[k] = v; };
  put('store', d.store || d.store_location || d.storeKey);
  put('device_id', d.device_id || d.uid || d.id);
  put('model', d.model || d.name);
  // Ár mindig szám (a `price` a UI-ban formázott string is lehet: „129 900 Ft”)
  const rawPrice = d.price_value ?? d.rawPrice ?? d.price;
  const numPrice = typeof rawPrice === 'number'
    ? rawPrice
    : Number(String(rawPrice ?? '').replace(/[^\d]/g, '')) || undefined;
  put('price', numPrice);
  put('condition', d.condition);
  // Akku százalék számként („92%” → 92)
  const rawBatt = d.battery_health ?? d.battery;
  const numBatt = typeof rawBatt === 'number'
    ? rawBatt
    : Number(String(rawBatt ?? '').replace(/[^\d]/g, '')) || undefined;
  put('battery_health', numBatt);
  // A foglalás szerver-oldali azonosítója. EZ AZ ÖSSZEKÖTŐ KULCS a GA4/PostHog
  // esemény és a Lead Center rekord között – enélkül a „foglalás → átvétel”
  // arány felületre bontva nem számolható ki.
  put('reservation_id', d.reservation_id);
  put('source_page', d.source_page || sourcePage());
  return out;
};
// Microsoft Clarity egyedi esemény (P4.2) – a session-felvételek üzleti
// eseményekkel címkézhetők. Consent előtt no-op (a clarity.js kezeli).
const cl  = (event) => { dbg('clarity', event, null); clarityEvent(event); };

/** Nyers GA4 egyedi esemény (pl. battery_* landing események). Ez a KÖZÖS
 *  belépési pont – a komponensek NE definiáljanak saját gtag-wrappert. */
export const trackGA4 = (event, params = {}) => { ga(event, params); };

// ── Google Ads konverziók ─────────────────────────────────────────────────────
// MINDEN konverziós címke egy helyen. Eseménytípusonként külön Google Ads
// konverziós művelet. Ha újat hozol létre, csak az "AW-606697580/" utáni
// címkét másold be ide a megfelelő sorba.
// Sprint 2C / FELADAT 5: a konverziós azonosítók KONFIGURÁLHATÓK. Amíg nincs
// külön Ads-művelet létrehozva, a jelenlegi (közös) címke marad – a működés
// változatlan. Új művelet bevezetése = CSAK env-változó beállítása, kódmódosítás
// nélkül (a CRA build-időben süti be a REACT_APP_* értékeket).
const ADS_ID = process.env.REACT_APP_ADS_ID || 'AW-606697580';
// A jelenlegi, minden kontakt-konverzióra használt közös címke.
const ADS_SHARED_LABEL = 'OSyKCJza2LAcEOzwpaEC';
const ADS_LABELS = {
  coupon:      process.env.REACT_APP_ADS_COUPON_CONVERSION      || '3quYCOjk1_kYEOzwpaEC',
  call:        process.env.REACT_APP_ADS_CALL_CONVERSION        || ADS_SHARED_LABEL,
  messenger:   process.env.REACT_APP_ADS_MESSENGER_CONVERSION   || ADS_SHARED_LABEL,
  calculator:  process.env.REACT_APP_ADS_CALCULATOR_CONVERSION  || ADS_SHARED_LABEL,
  callback:    process.env.REACT_APP_ADS_CALLBACK_CONVERSION    || ADS_SHARED_LABEL,
  reservation: process.env.REACT_APP_ADS_RESERVATION_CONVERSION || ADS_SHARED_LABEL,
};

/** Van-e DEDIKÁLT (a közöstől eltérő) Ads-művelet erre a típusra? Ha nincs,
 *  a lead-események nem lőnek külön konverziót – nem hígítjuk a meglévőt. */
const hasDedicatedAds = (type) => Boolean(ADS_LABELS[type]) && ADS_LABELS[type] !== ADS_SHARED_LABEL;
const ads = (type) => { try { if (prod && window.gtag && ADS_LABELS[type]) window.gtag('event', 'conversion', { send_to: `${ADS_ID}/${ADS_LABELS[type]}` }); } catch {} };

// ── NorbiPhone specifikus események ──────────────────────────────────────────

/** Kalkulátor: ár megjelent → legértékesebb esemény */
export const trackCalculatorResult = (brand, model, price) => {
  if (!allowFire(`calc_result|${brand}|${model}|${price}`, 'click')) return;
  fb('ViewContent', { content_name: `${brand} ${model}`, value: price, currency: 'HUF', content_category: 'felvasarlas' });
  tt('ViewContent', { content_id: model, value: price, currency: 'HUF' });
  ga('view_item', { item_name: `${brand} ${model}`, value: price, currency: 'HUF' });
};

/** Kalkulátor: email megadva → lead */
export const trackCalculatorLead = (brand, model, price) => {
  if (!allowFire(`calc_lead|${brand}|${model}`, 'click')) return;
  fb('Lead', { content_name: `${brand} ${model}`, value: price, currency: 'HUF' });
  tt('SubmitForm', { content_id: model, value: price, currency: 'HUF' });
  ga('generate_lead', { value: price, currency: 'HUF' });
  ads('calculator'); // Google Ads konverzió – kalkulátor lead
  cl('Contact_Form_Submit');
};

/** Hívás gomb. `ctx` (opcionális) a készülék/bolt kontextus – lásd deviceParams.
 *  A 2. paraméter visszafelé kompatibilis: a régi `trackCallClick('forras')`
 *  hívások változatlanul működnek. */
export const trackCallClick = (source = '', ctx = null) => {
  const p = ctx ? deviceParams(ctx) : { source_page: sourcePage() };
  if (!allowFire(`call|${source}|${p.device_id || ''}`, 'click')) return;
  fb('Contact', { content_name: `hivas_${source}` });
  tt('Contact', { content_name: `hivas_${source}` });
  ga('call_click', { event_label: source, ...p });
  ph('call_click', { source, ...p });
  ads('call');
  cl('PhoneCall_Click');
  if (/tesco|belvaros|store|uzlet/i.test(source) || p.store) cl('Store_Select');
};

/** Messenger gomb */
export const trackMessengerClick = (source = '', ctx = null) => {
  const p = ctx ? deviceParams(ctx) : { source_page: sourcePage() };
  if (!allowFire(`messenger|${source}|${p.device_id || ''}`, 'click')) return;
  fb('Contact', { content_name: `messenger_${source}` });
  tt('Contact', { content_name: `messenger_${source}` });
  ga('messenger_click', { event_label: source, ...p });
  ph('messenger_click', { source, ...p });
  ads('messenger'); // Messenger is kontakt-konverzió
  cl('Messenger_Click');
};

/** Termékoldal/termék-modál megtekintés – készülékenként EGYSZER (FELADAT 4).
 *  A `ctx` a listing/device objektum (store, uid, name, rawPrice, condition, battery). */
/**
 * Termékmegtekintés: `product_view` + a GA4 e-commerce `view_item` párja.
 *
 * DEDUP: `scope` nélkül készülékenként egyszer / útvonal – ez a LISTA-MODÁL
 * szándékos viselkedése (ugyanaz a darab újranyitva nem duplikál), változatlan.
 *
 * A TERMÉKLAP `scope`-ot ad: megtekintésenként egyedi, BELSŐ kulcsot (nem
 * kerül az eseménybe). Enélkül a „termék A → lista → termék A” SPA-úton a
 * második megtekintés elveszett (mérve: 1 esemény a várt 2 helyett), mert az
 * útvonal-alapú dedup csak akkor ürül, ha közben egy MÁSIK dedupolt esemény
 * tüzel – egy egyszerű listanézegetés ezt nem váltja ki.
 *
 * A payload és az eseménynevek VÁLTOZATLANOK.
 */
export const trackProductView = (ctx = {}, scope = '') => {
  const p = deviceParams(ctx);
  if (!allowFire(`product_view|${p.device_id || p.model || ''}|${scope}`, 'once')) return;
  ga('product_view', p);
  ph('product_view', p);
  // GA4 e-commerce megfelelő (a meglévő view_item riportok ne szakadjanak meg)
  ga('view_item', {
    currency: 'HUF',
    value: p.price,
    items: [{ item_id: p.device_id, item_name: p.model, price: p.price }],
  });
};

/**
 * A terméklap mért CTA-i – EGY forrás a hook-hívásokhoz és a riporthoz.
 *
 * A `cta_view` (a CTA LÁTSZOTT) és a kattintás SZÁNDÉKOSAN más eseménynév: a
 * kattintások meglévő, élő események (a riportok és a hirdetési jelek rájuk
 * épülnek), ezeket nem nevezzük át egy egységes `cta_click` kedvéért.
 * Az összerendelés ezért itt, kódban rögzített:
 *
 *   cta_view  cta=termek_foglalas   →  reservation_open
 *   cta_view  cta=termek_hivas      →  call_click       (event_label=telefon_reszletek)
 *   cta_view  cta=termek_messenger  →  messenger_click  (event_label=telefon_reszletek)
 *
 * Lásd docs/cro-audit-cta.md → „CTA mérési modell”.
 */
export const TERMEK_CTA = Object.freeze({
  FOGLALAS: 'termek_foglalas',
  HIVAS: 'termek_hivas',
  MESSENGER: 'termek_messenger',
});

/** cta_view `cta` → a hozzá tartozó MEGLÉVŐ kattintás-esemény neve. */
export const CTA_KATTINTAS_ESEMENY = Object.freeze({
  [TERMEK_CTA.FOGLALAS]: 'reservation_open',
  [TERMEK_CTA.HIVAS]: 'call_click',
  [TERMEK_CTA.MESSENGER]: 'messenger_click',
});

/**
 * CTA MEGJELENÍTVE – a mérés hiányzó nevezője.
 *
 * Eddig csak a kattintást mértük. Ebből nem derül ki, hogy egy alacsony
 * kattintásszám azért van-e, mert a gomb rossz, vagy mert a látogatók el sem
 * jutottak odáig. A `cta_view` / kattintás hányados az első szám, ami
 * bármilyen CTA-változtatás megítéléséhez kell.
 *
 * SZÁNDÉKOSAN CSAK GA4 (és PostHog). Megjelenés NEM konverzió:
 *  - Google Ads konverziót NEM lő (nem hígítjuk a meglévő műveletet),
 *  - Meta/TikTok eseményt NEM küld (egy impresszió nem `Contact`, és a
 *    hirdetési optimalizálást félrevinné).
 *
 * PRIVACY-MINIMÁL: a `device_id` NEM megy ki. A terméklap egyetlen
 * azonosítója a storefront `unique_id`-je, ami az IMEI utolsó 4 jegye – a
 * CTA-teljesítmény méréséhez nincs rá szükség. (A többi eseményt, pl. a
 * `product_view`-ot, ez a döntés nem érinti.)
 *
 * DEDUP: oldalmegtekintésenként és CTA-nként egyszer. A `scope` a hívó
 * (useCtaView) által adott, MEGTEKINTÉSENKÉNT egyedi, belső kulcs – SOSEM
 * kerül az eseménybe. Enélkül a globális, útvonal-alapú dedup a
 * „termék → lista → ugyanaz a termék” SPA-úton a második megtekintést
 * elnyelte (mérve: +0 a várt +3 helyett), mert az útvonal-váltást csak egy
 * közben tüzelő másik esemény vette volna észre.
 *
 * @param cta    a CTA stabil azonosítója (TERMEK_CTA)
 * @param ctx    készülék-kontextus (deviceParams), device_id nélkül küldve
 * @param scope  belső, megtekintésenként egyedi dedup-kulcs (nem megy ki)
 */
export const trackCtaView = (cta = '', ctx = null, scope = '') => {
  if (!cta) return;
  if (!allowFire(`cta_view|${cta}|${scope}`, 'once')) return;
  const p = ctx ? deviceParams(ctx) : {};
  delete p.device_id;          // privacy-minimál: IMEI-származék nem megy ki
  ga('cta_view', { cta, ...p });
  ph('cta_view', { cta, ...p });
};

/** Foglalás-űrlap MEGNYITVA (a gombra kattintás). Szándék-esemény, NEM lead.
 *
 *  Miért kell külön: a `reservation_submit` csak a SIKERES beküldést méri, így
 *  egy alacsony foglalásszám kétértelmű – nem derül ki, hogy senki nem nyomta
 *  meg a gombot, vagy mindenki megnyomta és az űrlapnál lépett ki. A kettő
 *  különbsége (open − submit) az elhagyási arány, ami eldönti, hogy a CTA
 *  helyén vagy az űrlapon kell változtatni.
 *
 *  Szándékosan CSAK GA4 + PostHog: se Ads-konverzió, se Meta/TikTok lead, hogy
 *  ne hígítsa a valódi konverziókat. */
export const trackReservationOpen = (ctx = {}) => {
  const p = deviceParams(ctx);
  if (!allowFire(`reservation_open|${p.device_id || ''}`, 'click')) return;
  ga('reservation_open', p);
  ph('reservation_open', p);
};

/** Készülékfoglalás elküldve (Sprint 2A). Lead-értékű esemény: GA4 + PostHog +
 *  Meta/TikTok kontakt. Beküldésenként egyszer (a dupla-submit ellen a gomb is
 *  tiltva van, ez a második védvonal). */
export const trackReservationSubmit = (ctx = {}) => {
  const p = deviceParams(ctx);
  if (!allowFire(`reservation_submit|${p.device_id || ''}`, 'click')) return;
  ga('reservation_submit', p);
  ph('reservation_submit', p);
  fb('Lead', { content_name: `foglalas_${p.model || ''}`, value: p.price, currency: 'HUF' });
  tt('SubmitForm', { content_id: p.device_id, value: p.price, currency: 'HUF' });
  cl('Reservation_Submit');
  if (hasDedicatedAds('reservation')) ads('reservation');   // csak dedikált Ads-művelettel
  trackLeadSubmit('reservation', ctx);      // egységes lead-esemény (2B/F5)
};

/** Visszahívás-kérés elküldve (Sprint 2A). */
export const trackCallbackSubmit = (ctx = {}) => {
  const p = { ...deviceParams(ctx), service_type: ctx.service_type || undefined };
  if (!allowFire(`callback_submit|${p.service_type || ''}|${p.source_page}`, 'click')) return;
  ga('callback_submit', p);
  ph('callback_submit', p);
  fb('Lead', { content_name: `visszahivas_${p.service_type || ''}` });
  tt('SubmitForm', { content_id: p.service_type });
  cl('Callback_Submit');
  if (hasDedicatedAds('callback')) ads('callback');         // csak dedikált Ads-művelettel
  trackLeadSubmit('callback', ctx);         // egységes lead-esemény (2B/F5)
};

// ── Egységes lead-események (Sprint 2B / FELADAT 5) ─────────────────────────
// MINDEN lead ugyanazt a 4 dimenziót küldi, hogy a GA4/Ads/PostHog riportokban
// összehasonlíthatók legyenek:
//   lead_source (melyik oldalról), lead_type (reservation|callback|calculator),
//   store, value + currency (Google Ads konverzió-értékhez).
const leadParams = (leadType, ctx = {}) => {
  const p = deviceParams(ctx);
  const out = {
    lead_type: leadType,
    lead_source: ctx.lead_source || p.source_page || sourcePage(),
    ...p,
  };
  // Google Ads / GA4 konverzió-érték: mindig `value` + `currency`.
  const v = ctx.value ?? ctx.offer_price ?? p.price;
  if (typeof v === 'number' && v > 0) { out.value = v; out.currency = 'HUF'; }
  return out;
};

/** Közös lead-esemény minden típusra – a típus-specifikus trackerek ezt hívják. */
export const trackLeadSubmit = (leadType, ctx = {}) => {
  const p = leadParams(leadType, ctx);
  if (!allowFire(`lead_submit|${leadType}|${p.device_id || p.model || ''}`, 'click')) return;
  ga('lead_submit', p);
  ph('lead_submit', p);
};

/** Kalkulátor: „Kérsz ajánlatot?” űrlap elküldve (Sprint 2B / FELADAT 1). */
export const trackCalculatorLeadSubmit = (ctx = {}) => {
  const p = leadParams('calculator', ctx);
  if (!allowFire(`calculator_lead_submit|${p.model || ''}`, 'click')) return;
  ga('calculator_lead_submit', p);
  ph('calculator_lead_submit', p);
  fb('Lead', { content_name: `felvasarlas_${p.model || ''}`, value: p.value, currency: 'HUF' });
  tt('SubmitForm', { content_id: p.model, value: p.value, currency: 'HUF' });
  ads('calculator');           // Google Ads konverzió (meglévő címke)
  cl('Calculator_Lead_Submit');
  trackLeadSubmit('calculator', ctx);
};

/** Árlista megnyitása (szerviz ártáblák, „Árak megtekintése”) – oldalanként egyszer. */
export const trackPriceListOpen = (source = '', ctx = null) => {
  const p = ctx ? deviceParams(ctx) : { source_page: sourcePage() };
  if (!allowFire(`price_list_open|${source}`, 'once')) return;
  ga('price_list_open', { event_label: source, ...p });
  ph('price_list_open', { source, ...p });
  cl('PriceList_Open');
};

/** Felvásárlás-oldal szándék-eseményei (a szerviz landing mintájára).
 *  A meglévő események (call_click, callback_submit, Ads-konverzió) ettől
 *  függetlenül, változatlanul tüzelnek – ezek KIEGÉSZÍTŐ, nevesített szándékok
 *  a kampány-riportokhoz. Messenger-kattintás SZÁNDÉKOSAN nincs köztük. */
export const trackBuybackCall = () => {
  if (!allowFire('felvasarlas_hivas', 'click')) return;
  ga('felvasarlas_hivas', { event_label: 'felvasarlas_oldal' });
  ph('felvasarlas_hivas', { source_page: sourcePage() });
  fbc('felvasarlas_hivas');
  tt('Contact', { content_name: 'felvasarlas_hivas' });
};

export const trackBuybackCallback = () => {
  if (!allowFire('felvasarlas_visszahivas', 'click')) return;
  ga('felvasarlas_visszahivas', { event_label: 'felvasarlas_oldal' });
  ph('felvasarlas_visszahivas', { source_page: sourcePage() });
  fbc('felvasarlas_visszahivas');
  tt('SubmitForm', { content_name: 'felvasarlas_visszahivas' });
};

export const trackBuybackEstimateClick = (source = '') => {
  if (!allowFire(`felvasarlas_arbecsles_kattintas|${source}`, 'click')) return;
  ga('felvasarlas_arbecsles_kattintas', { event_label: source || 'felvasarlas_oldal' });
  ph('felvasarlas_arbecsles_kattintas', { source, source_page: sourcePage() });
  fbc('felvasarlas_arbecsles_kattintas', { source });
  tt('ClickButton', { content_name: 'felvasarlas_arbecsles_kattintas' });
};

// ── /szerviz oldal szándék-események ─────────────────────────────────────────
// UGYANAZOK az eseménynevek, mint a szerviz-landingen (landing/index.html:
// szerviz_hivas, szerviz_visszahivas, hasznalt_atkattintas), így a kampány- és
// az organikus forgalom egy riportban összeadható. Új eseménynevet NE vezess be.

export const trackServiceCall = (source = '') => {
  if (!allowFire(`szerviz_hivas|${source}`, 'click')) return;
  ga('szerviz_hivas', { event_label: source || 'szerviz_oldal' });
  ph('szerviz_hivas', { source, source_page: sourcePage() });
  fbc('szerviz_hivas');
  tt('Contact', { content_name: 'szerviz_hivas' });
};

export const trackServiceCallback = () => {
  if (!allowFire('szerviz_visszahivas', 'click')) return;
  ga('szerviz_visszahivas', { event_label: 'szerviz_oldal' });
  ph('szerviz_visszahivas', { source_page: sourcePage() });
  fbc('szerviz_visszahivas');
  tt('SubmitForm', { content_name: 'szerviz_visszahivas' });
};

export const trackUsedPhonesClick = (source = '') => {
  if (!allowFire(`hasznalt_atkattintas|${source}`, 'click')) return;
  ga('hasznalt_atkattintas', { event_label: source || 'szerviz_oldal' });
  ph('hasznalt_atkattintas', { source, source_page: sourcePage() });
  fbc('hasznalt_atkattintas');
  tt('ClickButton', { content_name: 'hasznalt_atkattintas' });
};

/** Általános CTA gomb (pl. „Kérek bevizsgálást”, „Megnézem a fóliákat”).
 *  Mikrokonverzió / funnel-mérés: GA4 + TikTok engagement. Szándékosan NEM küld
 *  Google Ads konverziót vagy Meta Lead-et, hogy ne hígítsa a valódi konverziókat. */
export const trackCtaClick = (source = '') => {
  if (!allowFire(`cta|${source}`, 'click')) return;
  ga('cta_click', { event_label: source });
  tt('ClickButton', { content_name: source });
  cl('Service_CTA');
};

/** Lista-szűrő gomb (pl. telefonok kategória). Könnyű GA4 engagement esemény. */
export const trackFilterClick = (value = '') => {
  if (!allowFire(`filter|${value}`, 'click')) return;
  ga('filter_click', { event_label: value });
};

/** Google-értékelések megnyitása (külső link a GBP-re) – bizalmi mikrokonverzió. */
export const trackGoogleReviewsOpen = (source = '') => {
  if (!allowFire(`reviews_open|${source}`, 'click')) return;
  ga('google_reviews_open', { event_label: source });
  cl('GoogleReviews_Open');
};

/** Fiókpénz funnel lépés-szintű esemény (GA4). A teljes tölcsér drop-off mérése:
 *  calc_start, calc_brand_select, calc_model_select, calc_storage_select, calc_unlock,
 *  calc_water, calc_screen, calc_screenbugs, calc_frame, calc_back, calc_battery,
 *  calc_other, calc_email_view, calc_email_submit, calc_result, request_offer,
 *  calc_qualify, calc_payout_choice. Paraméterek tetszőlegesek (brand, model, value, …). */
export const trackCalcFunnel = (event, params = {}) => {
  // A funnel-lépések ismételhetők (a user visszaléphet), de EGY interakcióból
  // ne menjen ki kétszer: a kulcs a lépés + a fő paraméterek.
  if (!allowFire(`calcfunnel|${event}|${params.model || ''}|${params.value || ''}`, 'click')) return;
  ga(event, params);
  if (event === 'calc_start') cl('Buyback_Start');
};

/** Útvonalterv (Google Maps) gomb – üzletlátogatás-szándék (helyi mikrokonverzió).
 *  GA4 + Meta FindLocation + TikTok. Szándékosan NEM küld Google Ads konverziót
 *  (nincs dedikált címke); ha kell, az ADS_LABELS-be vehető fel egy 'directions'. */
export const trackRouteClick = (source = '', ctx = null) => {
  const p = ctx ? deviceParams(ctx) : { source_page: sourcePage() };
  if (!allowFire(`route|${source}|${p.store || ''}`, 'click')) return;
  fb('FindLocation', { content_name: `utvonal_${source}` });
  tt('ClickButton', { content_name: `utvonal_${source}` });
  ga('route_click', { event_label: source, ...p });
  ph('route_click', { source, ...p });
  // Régi GA4 esemény-név megtartva, hogy a meglévő riportok/célok ne szakadjanak meg.
  ga('directions_click', { event_label: source, ...p });
  cl('Directions_Click');
  if (/tesco|belvaros|store|uzlet/i.test(source) || p.store) cl('Store_Select');
};

/** @deprecated Használd a trackRouteClick-et. Alias a visszafelé kompatibilitásért. */
export const trackDirectionsClick = trackRouteClick;

/** VIP feliratkozás / kupon igénylés → lead + konverzió */
export const trackNewsletterSignup = (source = '') => {
  if (!allowFire(`newsletter|${source}`, 'click')) return;
  fb('Lead', { content_name: `vip_${source}` });
  fb('CompleteRegistration', { content_name: `vip_${source}` });
  tt('CompleteRegistration');
  ga('sign_up', { method: source });
  ga('generate_lead', { event_label: source });
  ads('coupon'); // Google Ads konverzió – kupon / popup / feliratkozás
  cl('VIP_Join');
};

// ── Sprint 5: kiegészítő mérési pontok ───────────────────────────────────────
// MINDEGYIKHEZ VAN LÉTEZŐ FELÜLET a projektben – „előre” tracker nem készül.

/** Belső keresés (jelenleg: a /szerviz árlista-kereső). A `results` a találati
 *  szám: a 0-találatos keresés mutatja, mit keresnek hiába a látogatók. */
export const trackSearch = (query = '', results = null, source = '') => {
  const q = String(query).trim().toLowerCase().slice(0, 60);
  if (!q) return;
  if (!allowFire(`search|${source}|${q}`, 'click')) return;
  const p = { search_term: q, source, source_page: sourcePage() };
  if (typeof results === 'number') p.results = results;
  ga('search', p);
  ph('search', p);
  fbc('search', { search_string: q });
  if (results === 0) cl('Search_NoResult');
};

/**
 * Készlet-kereső (/telefonok) – három külön esemény, ahogy a mérési terv kéri:
 *
 *   phone_search         – a látogató elkezdett keresni (keresési munkamenetenként
 *                          EGYSZER, az első leütéskor). Ez a nevező.
 *   phone_search_result  – volt találat. `search_term` + `results`.
 *   phone_search_empty   – NEM volt találat. Ez a legértékesebb: megmutatja,
 *                          milyen készüléket keresnek nálunk hiába.
 *
 * A result/empty a lekérdezésre deduplikált (`allowFire` 'click' mód), így a
 * gépelés közbeni köztes állapotok nem küldenek külön eseményt. A SZŰRÉS maga
 * változatlanul azonnali – a dedup csak a mérésre vonatkozik.
 */
/** Összehasonlító-választó: a látogató két készüléket választott és
 *  megnyitotta az összevetést. CSAK GA4 + PostHog – NEM konverzió, hirdetési
 *  platform felé nem megy. A `generated` mutatja, hogy kész (indexelt) oldalra
 *  ment-e, vagy egyedi párra – az utóbbiak listája jelzi, miből érdemes
 *  valódi összehasonlító oldalt csinálni. */
export const trackCompareSelect = (pair = '', generated = false) => {
  if (!pair) return;
  if (!allowFire(`compare_select|${pair}`, 'click')) return;
  const p = { pair, generated: !!generated, source_page: sourcePage() };
  ga('compare_select', p);
  ph('compare_select', p);
};

export const trackPhoneSearchStart = (source = 'telefonok') => {
  if (!allowFire(`phone_search_start|${source}`, 'click')) return;
  const p = { source, source_page: sourcePage() };
  ga('phone_search', p);
  ph('phone_search', p);
};

export const trackPhoneSearchResult = (query = '', results = 0, source = 'telefonok') => {
  const q = String(query).trim().toLowerCase().slice(0, 60);
  if (!q) return;
  const empty = results === 0;
  const event = empty ? 'phone_search_empty' : 'phone_search_result';
  if (!allowFire(`${event}|${source}|${q}`, 'click')) return;
  const p = { search_term: q, results, source, source_page: sourcePage() };
  ga(event, p);
  ph(event, p);
  if (empty) cl('Phone_Search_NoResult');
};

/** Kifelé mutató link (Facebook, TikTok, Instagram, Google Maps/vélemények).
 *  A hostot külön küldjük, hogy a riportban ne kelljen URL-t parse-olni. */
export const trackOutbound = (url = '', source = '') => {
  if (!url) return;
  let host = '';
  try { host = new URL(url, window.location.origin).host; } catch { host = ''; }
  if (!allowFire(`outbound|${host}|${source}`, 'click')) return;
  const p = { link_url: String(url).slice(0, 200), link_domain: host, source, source_page: sourcePage() };
  ga('outbound_click', p);
  ph('outbound_click', p);
};

/** GYIK/accordion kibontás. CSAK a nyitás mérődik (a csukás nem szándék-jel). */
export const trackFaqOpen = (question = '', source = '') => {
  const q = String(question).slice(0, 80);
  if (!q) return;
  if (!allowFire(`faq|${source}|${q}`, 'once')) return;   // kérdésenként egyszer/oldal
  const p = { faq_question: q, source, source_page: sourcePage() };
  ga('faq_open', p);
  ph('faq_open', p);
  cl('FAQ_Open');
};

/** Vélemény-carousel lapozás (nyíl vagy pötty). `direction`: next | prev | dot. */
export const trackReviewNav = (direction = '', source = '') => {
  if (!allowFire(`review_nav|${source}|${direction}`, 'click')) return;
  const p = { direction, source, source_page: sourcePage() };
  ga('review_nav', p);
  ph('review_nav', p);
};

/**
 * Az üzlet-bemutató blokk a képernyőre került. Ez a nevező: enélkül a
 * 360-megnyitások aránya értelmezhetetlen. Oldalanként egyszer.
 */
export const trackStoreGalleryVisible = (source = '') => {
  if (!allowFire(`store_gallery_visible|${source}`)) return;   // 'once'
  const p = { source, source_page: sourcePage() };
  ga('store_gallery_visible', p);
  ph('store_gallery_visible', p);
};

/** Kattintás egy üzlet-kártyán (üzletenként). A `store` a bolt kulcsa. */
export const trackStoreGalleryClick = (store = '', source = '') => {
  if (!allowFire(`store_gallery_click|${source}|${store}`, 'click')) return;
  const p = { store, source, source_page: sourcePage() };
  ga('store_gallery_click', p);
  ph('store_gallery_click', p);
};

/**
 * A vélemény-blokk TÉNYLEGESEN a képernyőre került (nem csak a DOM-ban van).
 * Ez a nevező: enélkül a Google-átkattintás aránya értelmezhetetlen, mert nem
 * tudni, hány látogató jutott el egyáltalán a blokkig. Oldalanként egyszer.
 */
export const trackReviewsVisible = (source = '') => {
  if (!allowFire(`reviews_visible|${source}`)) return;   // 'once' mód
  const p = { source, source_page: sourcePage() };
  ga('reviews_visible', p);
  ph('reviews_visible', p);
};

/**
 * A látogató TÖBB véleményt kért (léptetés a kártyák között). A `review_nav`
 * az irányt méri (prev/next/dot); ez a szándékot: „ennyi nem volt elég”.
 * A kettő szándékosan külön esemény – a nav iránya UX-adat, az expand CRO-adat.
 */
export const trackReviewsExpand = (source = '') => {
  if (!allowFire(`reviews_expand|${source}`)) return;    // munkamenetenként egyszer
  const p = { source, source_page: sourcePage() };
  ga('reviews_expand', p);
  ph('reviews_expand', p);
};

/** VIP-popup életciklus: open | close | field | success | error.
 *  A `success` a MEGLÉVŐ trackNewsletterSignup MELLETT fut (az a konverzió,
 *  ez a funnel-lépés) – külön eseménynév, tehát nincs duplikált küldés. */
// ── VIP kaparós sorsjegy funnel ──────────────────────────────────────────────
// A LEGFONTOSABB szám a `vip_prize_revealed → vip_subscribed` lemorzsolódás:
// ha sokan látják a nyereményt és mégsem adják meg az e-mailt, akkor a
// NYEREMÉNYEK nem elég vonzók – nem a mechanika a baj. Ezt a két esemény
// aránya mondja meg, ezért kapnak külön nevet a régi `vip_popup` mellett.
//
// A `vip_subscribed` NEM konverzió-esemény: a Google Ads konverzió továbbra is
// a meglévő `trackNewsletterSignup` (sign_up), hogy a historikus adat ne
// szakadjon meg. Ez a három csak a tölcsért méri.

/** A látogató elkezdte a kaparást (vagy megnyomta a „Felfedem” gombot). */
export const trackVipScratchStarted = (source = '') => {
  if (!allowFire(`vip_scratch_started|${source}`, 'click')) return;
  const p = { source: source || undefined, source_page: sourcePage() };
  ga('vip_scratch_started', p);
  ph('vip_scratch_started', p);
};

/** A szerver visszaadta a nyereményt, a látogató LÁTJA. */
export const trackVipPrizeRevealed = (prize = '', source = '') => {
  if (!allowFire(`vip_prize_revealed|${prize}`, 'click')) return;
  const p = { prize: prize || undefined, source: source || undefined, source_page: sourcePage() };
  ga('vip_prize_revealed', p);
  ph('vip_prize_revealed', p);
};

/** Megadta az e-mailt, a kupon kiállt. `repeat`: ismételt beküldés (idempotens). */
export const trackVipSubscribed = (prize = '', source = '', repeat = false) => {
  if (!allowFire(`vip_subscribed|${prize}|${repeat}`, 'click')) return;
  const p = {
    prize: prize || undefined,
    source: source || undefined,
    repeat: repeat || undefined,
    source_page: sourcePage(),
  };
  ga('vip_subscribed', p);
  ph('vip_subscribed', p);
};

export const trackVipPopup = (step = '', detail = '') => {
  if (!step) return;
  if (!allowFire(`vip_popup|${step}|${detail}`, step === 'field' ? 'once' : 'click')) return;
  const p = { step, detail: detail || undefined, source_page: sourcePage() };
  ga('vip_popup', p);
  ph('vip_popup', p);
  if (step === 'error') cl('VIP_Popup_Error');
};

/** Megosztás. `method`: native (Web Share API) | copy (vágólap) | error.
 *  A `ctx` a készülék-kontextus (model, device_id, price…). */
export const trackShare = (method = '', ctx = {}) => {
  const p = { ...deviceParams(ctx), method };
  if (!allowFire(`share|${method}|${p.device_id || p.source_page}`, 'click')) return;
  ga('share', p);
  ph('share', p);
  fbc('share', { content_name: p.model, method });
  cl('Share_Click');
};

/** 360° üzletbejárás. `step`: open | loaded | close | error.
 *  A `loaded` a betöltési időt is viszi (load_time_ms) – ebből derül ki, ha
 *  egy panoráma túl nagy és lassan jön be. */
export const trackStore360 = (step = '', ctx = {}) => {
  if (!step) return;
  const p = {
    store_id: ctx.storeId || undefined,
    store_name: ctx.storeName || undefined,
    device_type: undefined,          // a commonParams tölti ki
    source_page: sourcePage(),
  };
  if (typeof ctx.loadTimeMs === 'number') p.load_time_ms = ctx.loadTimeMs;
  if (ctx.reason) p.reason = ctx.reason;
  // A megnyitás/bezárás ismételhető (több üzlet, visszatérés), de egy
  // interakcióból ne menjen ki kétszer.
  if (!allowFire(`store360|${step}|${p.store_id || ''}`, 'click')) return;
  ga(`store360_${step}`, p);
  ph(`store360_${step}`, p);
  if (step === 'open') { fbc('store360_open', { store: p.store_id }); cl('Store360_Open'); }
  if (step === 'error') cl('Store360_Error');
};

/** 404 – melyik URL-ek halnak el (átirányítás-lista alapja). GA4: page_not_found. */
export const track404 = (path = '') => {
  if (!allowFire(`404|${path}`, 'once')) return;
  ga('page_not_found', { page_path: path, page_referrer: document.referrer || '' });
};

/** Scroll-mélység mérés (25/50/75/100%) – GA4 `scroll_depth` esemény.
 *  Oldalanként (pathname) minden küszöb egyszer tüzel. Passive listener,
 *  requestAnimationFrame-mel throttle-olva → nincs INP-költség. */
export const initScrollDepthTracking = () => {
  if (!prod) return () => {};
  let fired = {};
  let currentPath = window.location.pathname;
  let ticking = false;

  const check = () => {
    ticking = false;
    if (window.location.pathname !== currentPath) {
      currentPath = window.location.pathname;
      fired = {};
    }
    const doc = document.documentElement;
    const scrollable = doc.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;
    const pct = Math.round(((window.scrollY || doc.scrollTop) / scrollable) * 100);
    for (const t of [25, 50, 75, 100]) {
      if (pct >= t && !fired[t]) {
        fired[t] = true;
        ga('scroll_depth', { percent: t, page_path: currentPath });
      }
    }
  };
  const onScroll = () => {
    if (!ticking) { ticking = true; requestAnimationFrame(check); }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  return () => window.removeEventListener('scroll', onScroll);
};
